import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeStyles = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm transition-opacity animate-fade-in" onClick={onClose} />
        
        <div className={`relative bg-white w-full ${sizeStyles[size]} transform transition-all animate-slide-up
          rounded-t-2xl sm:rounded-2xl shadow-elevated
          max-h-[90vh] sm:max-h-[85vh] flex flex-col`}>
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-neutral-200/60 bg-gradient-to-r from-neutral-50/80 to-white rounded-t-2xl sm:rounded-t-2xl">
            <h3 className="text-base sm:text-lg font-semibold text-neutral-800">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="px-4 sm:px-6 py-4 sm:py-5 overflow-y-auto flex-1">
            {children}
          </div>
          
          {footer && (
            <div className="px-4 sm:px-6 py-4 border-t border-neutral-200/60 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 bg-neutral-50/50 rounded-b-2xl sm:rounded-b-2xl">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
